import React, { useState } from "react";
import {
  Box,
  ButtonBase,
  Collapse,
  Dialog,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getEventInfo, getFamilyColor } from "./eventDict";
import { ANALYTICS_FONT_FAMILY } from "./analyticsTypography";
import {
  DRILLDOWN_SHEET_BODY_SCROLL_SX,
  DRILLDOWN_SHEET_BORDER_RADIUS,
  DRILLDOWN_SHEET_PAPER_ELEVATION_SX,
} from "./analyticsDrilldownStyles";

/**
 * MongoDB ObjectId format check — 24 lowercase or uppercase hex characters.
 *
 * Mirrors the helper in FeedList.js. Used by the gallery-link fallback below:
 * the `/floor-gallery/:galleryId` route's lookup is ObjectId-only in practice,
 * so constructing a link with a UUID-shaped id renders an infinite spinner.
 * We only render the constructed fallback when the id is ObjectId-shaped.
 */
const isLikelyObjectId = (s) =>
  typeof s === "string" && /^[0-9a-fA-F]{24}$/.test(s);

/**
 * Guard for `detail.galleryUrl` on any event. Only gallery_ready events carry
 * a backend-issued gallery URL; other events may log an editor-scope URL (UUID
 * path segment, dashes) in this field. Verify the trailing path segment is an
 * ObjectId before treating the value as a gallery URL. Returns null for any
 * URL that fails the check so we fall through to groupGalleryUrl instead.
 */
const asValidGalleryUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  const trailing = url.split("?")[0].split("/").filter(Boolean).pop();
  return isLikelyObjectId(trailing) ? url : null;
};

const formatTimestamp = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const formatRelative = (iso) => {
  if (!iso) return "";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const Field = ({ label, children }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        display: "block",
        color: "rgba(0,0,0,0.5)",
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        fontSize: "0.65rem",
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Box sx={{ fontSize: "0.9rem", color: "rgba(0,0,0,0.88)", wordBreak: "break-word" }}>
      {children}
    </Box>
  </Box>
);

const JsonBlock = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const text = JSON.stringify(value || {}, null, 2);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      // ignore — clipboard may be blocked in iframes
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      <Tooltip title={copied ? "Copied" : "Copy"} placement="left">
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            color: "rgba(0,0,0,0.5)",
            bgcolor: "rgba(255,255,255,0.6)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
          }}
        >
          {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Tooltip>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          bgcolor: "rgba(0,0,0,0.03)",
          borderRadius: "10px",
          border: "1px solid rgba(0,0,0,0.06)",
          fontFamily: ANALYTICS_FONT_FAMILY,
          fontSize: "0.75rem",
          lineHeight: 1.5,
          color: "rgba(0,0,0,0.88)",
          overflowX: "auto",
          maxHeight: 320,
          overflowY: "auto",
        }}
      >
        {text}
      </Box>
    </Box>
  );
};

const EventDetailModal = ({ open, onClose, row, groupGalleryUrl }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [debugOpen, setDebugOpen] = useState(false);

  if (!row) return null;

  const eventKey = row.meta?.event;
  const info = getEventInfo(eventKey);
  const dotColor = getFamilyColor(eventKey);

  // `uniqueId` covers both field names — gallery_ready emits `detail.uniqueId`;
  // older events emit `detail.galleryId`. Fall back through both.
  const uniqueId = row.detail?.uniqueId || row.detail?.galleryId;
  // Only treat detail.galleryUrl as authoritative when it's a valid gallery URL
  // (ObjectId-based path, no UUID dashes). Editor events may log a non-gallery
  // URL in this field; asValidGalleryUrl returns null for those so the link
  // resolution falls through to the parent group's propagated galleryUrl.
  const galleryUrlValue = asValidGalleryUrl(row.detail?.galleryUrl);
  // Resolve the hyperlink for `uniqueId` and the standalone galleryUrl row.
  // Priority:
  //   1. row.detail.galleryUrl — only present on `gallery_ready`.
  //   2. groupGalleryUrl — authoritative URL inherited from the parent group
  //      (set by FeedList's capture + cross-group propagation steps), so any
  //      event in a session whose group has a known URL gets a working link.
  //   3. Constructed `/floor-gallery/{uniqueId}` — used ONLY when `uniqueId`
  //      is in MongoDB ObjectId shape (the route is ObjectId-only in practice;
  //      a UUID would render an infinite spinner). Mirrors the same guard
  //      applied in FeedList.js so the modal can't surface a broken link the
  //      feed card already suppresses.
  const galleryHref =
    galleryUrlValue ||
    groupGalleryUrl ||
    (uniqueId &&
    isLikelyObjectId(uniqueId) &&
    !String(uniqueId).includes("not-provided")
      ? `${window.location.origin}/floor-gallery/${uniqueId}`
      : null);

  const customer = row.meta?.customer_ref;
  const refLabel =
    customer && !String(customer).includes("not-provided") ? customer : "—";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: DRILLDOWN_SHEET_BORDER_RADIUS,
          ...DRILLDOWN_SHEET_PAPER_ELEVATION_SX,
          m: { xs: 0, sm: 2 },
          mt: { xs: "auto", sm: 2 },
          maxHeight: { xs: "92vh", sm: "85vh" },
          bgcolor: "#fff",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 2.5, sm: 3 },
          pt: { xs: 2.5, sm: 3 },
          pb: 2,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: dotColor,
                flexShrink: 0,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "1.05rem",
                  color: "rgba(0,0,0,0.88)",
                  lineHeight: 1.2,
                }}
                noWrap
              >
                {info.alias}
              </Typography>
              <Typography
                sx={{
                  fontFamily: ANALYTICS_FONT_FAMILY,
                  fontSize: "0.72rem",
                  color: "rgba(0,0,0,0.5)",
                  mt: 0.25,
                }}
                noWrap
              >
                {info.key}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ mt: 0, flexShrink: 0 }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={DRILLDOWN_SHEET_BODY_SCROLL_SX}>
        <Box
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: { xs: 2.5, sm: 3 },
            paddingBottom: { xs: "calc(env(safe-area-inset-bottom) + 24px)", sm: 3 },
          }}
        >
        <Stack spacing={2.5}>
          <Box
            sx={{
              p: 2,
              borderRadius: "10px",
              bgcolor: "rgba(0,0,0,0.025)",
              border: "1px solid rgba(0,0,0,0.05)",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: "rgba(0,0,0,0.5)",
                fontWeight: 600,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                fontSize: "0.65rem",
                mb: 0.5,
              }}
            >
              What this means
            </Typography>
            <Typography sx={{ fontSize: "0.9rem", color: "rgba(0,0,0,0.88)", lineHeight: 1.45 }}>
              {info.desc}
            </Typography>
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2.5}
            sx={{ "& > *": { flex: 1 } }}
          >
            <Field label="Timestamp">
              {formatTimestamp(row.timestamp)}
              {row.timestamp ? (
                <Box
                  component="span"
                  sx={{ ml: 0.75, color: "rgba(0,0,0,0.5)", fontSize: "0.8rem" }}
                >
                  · {formatRelative(row.timestamp)}
                </Box>
              ) : null}
            </Field>
            <Field label="Origin">
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  px: 1,
                  py: 0.25,
                  bgcolor: "rgba(0,0,0,0.05)",
                  borderRadius: "6px",
                  fontSize: "0.78rem",
                  fontWeight: 500,
                }}
              >
                {row.meta?.origin || "Unknown"}
              </Box>
            </Field>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2.5}
            sx={{ "& > *": { flex: 1 } }}
          >
            <Field label="Ref">
              <Box
                component="span"
                sx={{
                  fontFamily: ANALYTICS_FONT_FAMILY,
                  fontSize: "0.8rem",
                  color: refLabel === "—" ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.88)",
                }}
              >
                {refLabel}
              </Box>
            </Field>
            <Field label="User">{row.meta?.user || "Guest"}</Field>
          </Stack>

          {uniqueId && !String(uniqueId).includes("not-provided") && (
            <Field label="uniqueId">
              {galleryHref ? (
                <Box
                  component="a"
                  href={galleryHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    textDecoration: "none",
                    color: "#6366f1",
                    fontWeight: 500,
                    fontSize: "0.85rem",
                    fontFamily: ANALYTICS_FONT_FAMILY,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {uniqueId}
                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                </Box>
              ) : (
                <Box
                  component="span"
                  sx={{
                    fontFamily: ANALYTICS_FONT_FAMILY,
                    fontSize: "0.85rem",
                    color: "rgba(0,0,0,0.88)",
                  }}
                >
                  {uniqueId}
                </Box>
              )}
            </Field>
          )}
          {galleryUrlValue && (
            <Field label="galleryUrl">
              <Box
                component="a"
                href={galleryUrlValue}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  textDecoration: "none",
                  color: "#6366f1",
                  fontWeight: 500,
                  fontSize: "0.82rem",
                  wordBreak: "break-all",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {galleryUrlValue}
                <OpenInNewIcon sx={{ fontSize: 14, flexShrink: 0 }} />
              </Box>
            </Field>
          )}

          {row.detail?.fileName && (
            <Field label="File">
              <Box
                component="span"
                sx={{
                  fontFamily: ANALYTICS_FONT_FAMILY,
                  fontSize: "0.8rem",
                }}
              >
                {row.detail.fileName}
                {row.detail.fileSize ? ` · ${row.detail.fileSize}` : ""}
              </Box>
            </Field>
          )}

          {/* Debug data — collapsed by default; intended for developers only */}
          <Box>
            <ButtonBase
              onClick={() => setDebugOpen((v) => !v)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                borderRadius: "6px !important",
                textTransform: "none !important",
                px: 0.5,
                py: 0.25,
                color: "rgba(0,0,0,0.3)",
                "&:hover": { color: "rgba(0,0,0,0.5)" },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                  fontSize: "0.65rem",
                }}
              >
                Debug data
              </Typography>
              <ExpandMoreIcon
                sx={{
                  fontSize: 15,
                  transform: debugOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 200ms ease",
                }}
              />
            </ButtonBase>

            <Collapse in={debugOpen}>
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "rgba(0,0,0,0.3)",
                      fontWeight: 600,
                      letterSpacing: 0.4,
                      textTransform: "uppercase",
                      fontSize: "0.62rem",
                      mb: 0.5,
                    }}
                  >
                    Meta
                  </Typography>
                  <JsonBlock value={row.meta} />
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "rgba(0,0,0,0.3)",
                      fontWeight: 600,
                      letterSpacing: 0.4,
                      textTransform: "uppercase",
                      fontSize: "0.62rem",
                      mb: 0.5,
                    }}
                  >
                    Detail
                  </Typography>
                  <JsonBlock value={row.detail} />
                </Box>
              </Stack>
            </Collapse>
          </Box>
        </Stack>
        </Box>
      </Box>
    </Dialog>
  );
};

export default EventDetailModal;
